import {styled, css} from "styled-components";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import type {ReactNode} from "react";
import {Observer} from "mobx-react";
import type {AppState} from "../../../data/state/AppState";
import {HorizontalAlignment, VerticalAlignment} from "../../../utils/dom/DomUtils";
import type {Organization} from "../../../data/models/Organization";
import type {NavBarState} from "../../../StateManager";
import {useAppStore} from "../../../StateManager";
import {colorPalette} from "../styles/colorPalette";
import {VerticalFlexStyle, FlexCenterStyle, FlexStyle, Flex, baseDistance, radius, zIndex, fontWeight, fontSize} from "../styles/styles";
import type {IFlexStyle} from "../styles/styles";
import CirclePlusIcon from "../icons/circle-plus.svg?react";
import CircleQuestionIcon from "../icons/circle-question.svg?react";
import GearIcon from "../icons/gear.svg?react";
import ArrowDownToBracketIcon from "../icons/arrow-down-to-bracket.svg?react";
import SidebarCloseIcon from "../icons/sidebar-close.svg?react";
import HouseLineIcon from "../icons/house-line.svg?react";
import BriefCaseAltIcon from "../icons/briefcase-alt.svg?react";
import BookOpenIcon from "../icons/book-open.svg?react";
import ChartLineUpIcon from "../icons/chart-line-up.svg?react";
import {FeatureImportPanelV5} from "../abstract/view/FeatureImportPanelV5";
import SidebarOpenIcon from "../icons/sidebar.svg?react";
import LogoIcon from "../icons/xyicon_logo.svg?react";
import {useClickOutside, useCloseOnKeyboardPress, useMouseLeave} from "../utils";
import {CheckboxInputV5} from "../details/datatypes/CheckboxInputV5";
import {CreateViewPopup} from "../abstract/view/create/CreateViewPopup";
import {IconButtonV5} from "../interaction/IconButtonV5";
import {Functions} from "../../../utils/function/Functions";
import {pseudoLabelTooltipStyle} from "../Root.styled";
import {DropdownButtonStyled, DropdownButtonV5} from "../interaction/DropdownButtonV5";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import type {IViewFolder} from "../../../data/models/ViewUtils";
import type {View} from "../../../data/models/View";
import type {Report} from "../../../data/models/Report";
import {ViewItemSharingPopup} from "../sharing/ViewItemSharingPopup";
import {GlobalSharingPopup, type ViewActionType} from "../sharing/GlobalSharingPopup";
import {NavigationEnum, SettingsSubNav} from "../../../Enums";
import type {IDropdownElement} from "../dropdown/TopBarDropdownV5";
import {TopBarDropdownV5} from "../dropdown/TopBarDropdownV5";
import BugIcon from "../icons/bug.svg?react";
import LightbulbIcon from "../icons/lightbulb-alt.svg?react";
import NotebookIcon from "../icons/notebook.svg?react";
import KeyboardIcon from "../icons/keyboard.svg?react";
import MessageSquareExclamationIcon from "../icons/message-square-exclamation.svg?react";
import {TimeUtils} from "../../../utils/TimeUtils";
import {DropdownContentStyled} from "../dropdown/TopBarDropdown.styled";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import {ShortcutWindowV5} from "../popup/ShortcutWindowV5";
import {ReportPanelV5} from "../modules/report/create/ReportPanelV5";
import GlobeIcon from "../icons/universe.svg?react";
import {XyiconFeature} from "../../../generated/api/base";
import {getActiveWorkspaceViewTabFromLocalStorage, getWorkspaceViewTabsFromLocalStorage, onWorkspaceViewSelect} from "../topbar/ViewTabsCommon";
import {DateUtils} from "../../../utils/DateUtils";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import {SpaceSelectorPopup} from "./SpaceSelectorPopup";
import {PortfolioSelector} from "./PortfolioSelector";
import type {OnWorkspaceRenameActiveChange, WorkspaceViewType} from "./views/WorkspaceViewCommon";
import {NavigationViewSection} from "./views/NavigationViewSection";
import type {TMenuV5} from "./NavigationCommon";
import {NavMenuStyled} from "./NavigationCommon";
import {NavigationMenuItem} from "./NavigationMenuItem";
import {ViewFind} from "./ViewFind";
import {ViewSection} from "./views/ViewSection";
import {switchOrganization} from "./OrganizationSwitchUtils";

enum DatabaseEnv {
	Dev = 1,
	QA = 2,
	Prod = 3,
}

interface IGlobalViewSharingPopupState {
	readonly view: View | Report;
	readonly viewActionType: ViewActionType;
	readonly newName?: string;
}

type TNavigationV5 = {
	$navBarState?: NavBarState;
	$isHome?: boolean;
	appState?: AppState;
};

interface ISideBarWidgetsProps extends IFlexStyle {
	$isHome: boolean;
	$navBarState: NavBarState;
}

const mainNavMenusV5: TMenuV5[] = [
	{
		label: "Home",
		nav: NavigationEnum.HOME,
		icon: HouseLineIcon,
	},
	{
		label: "Portfolios",
		nav: NavigationEnum.PORTFOLIOS,
		icon: BriefCaseAltIcon,
	},
	{
		label: "Catalog",
		nav: NavigationEnum.CATALOG,
		icon: BookOpenIcon,
	},
	{
		label: "Reports",
		nav: NavigationEnum.REPORTS,
		icon: ChartLineUpIcon,
	},
];

const workspaceViewNavigationMenus: readonly NavigationEnum[] = [
	NavigationEnum.XYICONS,
	NavigationEnum.BOUNDARIES,
	NavigationEnum.REPORT_VIEWS,
	NavigationEnum.SPACE,
	NavigationEnum.SPACES,
];

const isWorkspaceViewNavigationMenu = (menu: NavigationEnum | null): boolean => {
	return !!menu && workspaceViewNavigationMenus.includes(menu);
};

const SeparatorV5 = (props: {readonly isDocked: boolean}) => {
	return (
		<SeparatorV5Styled $isDocked={props.isDocked}>
			<span></span>
		</SeparatorV5Styled>
	);
};

export const Navigation = () => {
	const appState = useAppStore((state) => state.appState);
	const navBarState = useAppStore((state) => state.navBarState);
	const viewForOpenSpaceSelector = useAppStore((state) => state.viewForOpenSpaceSelector);
	const isScopeWideSearchOpen = useAppStore((state) => state.isScopeWideSearchOpen);
	const [createViewPopupType, setCreateViewPopupType] = useState<WorkspaceViewType | null>(null);
	const [viewFolderIdToSaveNewViewInto, setViewFolderIdToSaveNewViewInto] = useState<string>("");
	const [viewItemForSharePanel, setViewItemForSharePanel] = useState<View | IViewFolder | Report | null>(null);
	const navigationRef = useRef<HTMLDivElement>(null);
	const spaceSelectorRef = useRef<HTMLDivElement>(null);
	const onlyIcon = navBarState === "closed";
	const isNavBarHidden = navBarState === "hidden";
	const viewsRef = useRef<HTMLDivElement>(null);
	const previousSelectedMenuRef = useRef<NavigationEnum | null>(null);
	const [importOpen, setImportOpen] = useState<boolean>(false);
	const [isHelpOpen, setIsHelpOpen] = useState<boolean>(false);
	const {localStorage: ls} = appState.app.transport.services;
	const setNavBarState = useAppStore((state) => state.setNavBarState);
	const setViewForOpenSpaceSelector = useAppStore((state) => state.setViewForOpenSpaceSelector);
	const setSearchWindowOpenState = useAppStore((state) => state.setIsScopeWideSearchOpen);
	const helpRef = useRef<HTMLDivElement>(null);
	const [isShortcutOpen, setIsShortcutOpen] = useState<boolean>(false);
	const [viewSearchString, setViewSearchString] = useState<string>("");
	const [newReport, setNewReport] = useState<Report>(null);
	const [favoriteViewFolderIdToSaveNewViewInto, setFavoriteViewFolderIdToSaveNewViewInto] = useState<string>(null);
	const [globalViewSharingPopupState, setGlobalViewSharingPopupState] = useState<IGlobalViewSharingPopupState | null>(null);
	const [isViewPopupOpen, setIsViewPopupOpen] = useState<boolean>(false);
	const [isWorkspaceRenameActive, setIsWorkspaceRenameActive] = useState<boolean>(false);
	const isWorkspaceRenameActiveRef = useRef<boolean>(false);

	const onWorkspaceRenameActiveChange = useCallback<OnWorkspaceRenameActiveChange>((isActive) => {
		isWorkspaceRenameActiveRef.current = isActive;
		setIsWorkspaceRenameActive(isActive);
	}, []);

	const onMouseLeave = () => {
		if (navBarState === "open" && !isViewPopupOpen && !isWorkspaceRenameActive && !isWorkspaceRenameActiveRef.current) {
			setNavBarState("closed");
			setIsHelpOpen(false);
		}
	};

	useMouseLeave(navigationRef, onMouseLeave, [appState.app.modalContainer]);

	useClickOutside([spaceSelectorRef, viewsRef, helpRef], () => {
		if (viewForOpenSpaceSelector) {
			setViewForOpenSpaceSelector(null);
		}
		if (isHelpOpen) {
			setIsHelpOpen(false);
		}
	});
	useCloseOnKeyboardPress(() => setIsHelpOpen(false), KeyboardListener.KEY_ESCAPE, [isHelpOpen], {
		enabled: isHelpOpen,
		eventType: "keydown",
		onlyLatest: true,
		stopPropagation: true,
	});

	const onCreateViewClick = () => {
		if (!createViewPopupType) {
			setCreateViewPopupType("My Views");
		}
	};

	const closeCreateViewPopup = () => {
		if (createViewPopupType) {
			setCreateViewPopupType(null);
		}
	};

	const openReportPanel = (report: Report, favoriteViewFolderIdToSaveNewViewInto: string = null, viewFolderIdToSaveNewViewInto: string = "") => {
		setNewReport(report);
		setFavoriteViewFolderIdToSaveNewViewInto(favoriteViewFolderIdToSaveNewViewInto);
		setViewFolderIdToSaveNewViewInto(viewFolderIdToSaveNewViewInto);
	};

	const secondaryMenu: TMenuV5[] = [
		{
			label: "Spaces",
			nav: NavigationEnum.SPACES,
			icon: GlobeIcon,
		},
		{
			label: "Create View",
			icon: CirclePlusIcon,
			onClick: onCreateViewClick,
		},
	];

	const widgetMenus: TMenuV5[] = [
		{
			label: "Import",
			icon: ArrowDownToBracketIcon,
			onClick: () => setImportOpen(true),
		},
		{
			label: "Help",
			icon: CircleQuestionIcon,
			onClick: () => setIsHelpOpen(true),
		},
		{
			label: "Settings",
			icon: GearIcon,
			onClick: () => {
				appState.app.navigation.goApp({
					mainNav: NavigationEnum.SETTINGS,
					subNavs: [SettingsSubNav.PERSONAL],
				});
			},
		},
	];

	const goApp = (menu: TMenuV5) => {
		if (menu.nav) {
			appState.app.navigation.goApp({
				mainNav: menu.nav,
			});

			if (isScopeWideSearchOpen) {
				setSearchWindowOpenState(false);
			}
		}
	};

	const onNavBarClick = () => {
		if (navBarState === "closed" && !importOpen) {
			setNavBarState("open");
		}
	};

	const changeDockedStatus = (newState: NavBarState) => {
		setNavBarState(newState);

		if (newState === "closed" || newState === "docked") {
			ls.set(ls.getV5NavigationPanelDockedStateKey(appState.user.id), newState);
		}
	};

	const {organizations, organizationIdByAuthData, selectedMenu, selectedFeature} = appState;

	useEffect(() => {
		if (appState.user?.id) {
			const savedValueForDockedState = ls.get(ls.getV5NavigationPanelDockedStateKey(appState.user.id));
			const isNavBarTemporarilyOpen = useAppStore.getState().navBarState === "open";
			const shouldKeepTemporarilyOpenPanel =
				isNavBarTemporarilyOpen &&
				// Keep this scoped to workspace-view-to-workspace-view switches.
				isWorkspaceViewNavigationMenu(previousSelectedMenuRef.current) &&
				isWorkspaceViewNavigationMenu(selectedMenu);

			if (savedValueForDockedState && selectedMenu !== NavigationEnum.SETTINGS && !shouldKeepTemporarilyOpenPanel) {
				setNavBarState(savedValueForDockedState);
			}
		}

		previousSelectedMenuRef.current = selectedMenu;
	}, [ls, appState.user?.id, setNavBarState, selectedMenu]);

	const dockNavBar = () => changeDockedStatus("docked");
	const undockNavBar = () => changeDockedStatus("closed");

	const sortedOrganizations = useMemo(() => {
		const array: Organization[] = (organizations ? [...organizations] : []).toSorted((a: Organization, b: Organization) =>
			StringUtils.sortIgnoreCase(a.name, b.name),
		);

		const item = array.find((o) => o.id === organizationIdByAuthData);

		if (!item) {
			return array;
		}

		const index = array.indexOf(item);

		if (index !== -1) {
			array.splice(index, 1);
			array.unshift(item);
		}
		return array;
	}, [organizations, organizationIdByAuthData]);

	const openCreateViewPopup = (type: WorkspaceViewType, viewFolderId: string) => {
		setCreateViewPopupType(type);
		setViewFolderIdToSaveNewViewInto(viewFolderId);
	};

	const onViewItemShareClick = (viewItem: View | IViewFolder | Report) => {
		setViewItemForSharePanel(viewItem);
	};

	const onGlobalViewActionClick = (view: View | Report, viewActionType: ViewActionType, newName?: string) => {
		setGlobalViewSharingPopupState({
			view,
			viewActionType,
			newName,
		});
	};

	const closeGlobalViewSharingPopup = () => {
		setGlobalViewSharingPopupState(null);
	};

	const onToggleLiveChatBox = () => {
		const liveChat: HTMLDivElement = document.querySelector("#hubspot-messages-iframe-container.widget-align-left");

		liveChat?.classList.toggle("open");
		setIsHelpOpen(false);
	};

	const onShortCutClicked = async () => {
		if (!isShortcutOpen) {
			setIsShortcutOpen(true);
		} else {
			setIsShortcutOpen(false);
			await TimeUtils.waitForNextFrame();
			setIsShortcutOpen(true);
		}
		setIsHelpOpen(false);
	};

	const onCloseShortcut = () => {
		setIsShortcutOpen(false);
	};

	const onDocsClicked = () => {
		setIsHelpOpen(false);
		appState.app.navigation.openInNewTab("https://support.xyicon.com");
	};

	const onFeatureClicked = () => {
		setIsHelpOpen(false);
		appState.app.navigation.openInNewTab("https://support.xyicon.com/docs/submit-a-feature-request");
	};

	const onBugClicked = () => {
		setIsHelpOpen(false);
		appState.app.navigation.openInNewTab("https://support.xyicon.com/docs/report-an-issue");
	};

	const helpElements: IDropdownElement[] = [
		{
			icon: <MessageSquareExclamationIcon />,
			onMouseDown: onToggleLiveChatBox,
			label: "Live Chat",
		},
		{
			icon: <KeyboardIcon />,
			onMouseDown: onShortCutClicked,
			label: "Keyboard Shortcuts",
		},
		{
			icon: <NotebookIcon />,
			onMouseDown: onDocsClicked,
			label: "Help Documents",
		},
		{
			icon: <LightbulbIcon />,
			onMouseDown: onFeatureClicked,
			label: "Suggest a Feature",
		},
		{
			icon: <BugIcon />,
			onMouseDown: onBugClicked,
			label: "Report a Problem",
		},
	];

	const onClickOrganizationSwitch = (org: Organization) => {
		switchOrganization(org, appState);
	};

	const onRunReport = async (report: Report) => {
		await appState.app.transport.services.feature.runSavedReport(report);
	};

	const actions = appState.actions;

	useEffect(() => {
		if (selectedFeature) {
			const viewMaybe = actions.getSelectedView(selectedFeature);
			const viewTabsInLocalStorage = getWorkspaceViewTabsFromLocalStorage(appState.user.id, organizationIdByAuthData, actions);
			const currentTab = getActiveWorkspaceViewTabFromLocalStorage(appState.user.id, organizationIdByAuthData);
			const isCurrentTabIdEqualWithViewId = currentTab?.viewId === viewMaybe?.id;
			const isViewTabsInLocalStorageContainsViewId = viewTabsInLocalStorage.find((vt) => vt.viewId === currentTab.viewId);

			if (selectedMenu === NavigationEnum.REPORT_VIEWS) {
				if (!isCurrentTabIdEqualWithViewId && !isViewTabsInLocalStorageContainsViewId) {
					const reportViews = actions
						.getReportViews()
						.toSorted((a, b) => DateUtils.sortByDate(new Date(a.lastModifiedAt), new Date(b.lastModifiedAt)));

					if (reportViews.length > 0) {
						onWorkspaceViewSelect(reportViews[0]);
					} else {
						appState.app.navigation.goToDefault();
					}
				}
			} else {
				if (viewMaybe && [XyiconFeature.Xyicon, XyiconFeature.Boundary, XyiconFeature.SpaceEditor].includes(selectedFeature)) {
					onWorkspaceViewSelect(viewMaybe);
				}
			}
		}
	}, [selectedMenu, selectedFeature, actions, appState, organizationIdByAuthData]);

	return (
		<Observer>
			{() => {
				// !isHome means that we are in the Workspace, in Portfolio-Wide data, not in Org-Wide collections/data
				const isHome = ["portfolios", "catalog", "reports", "home"].includes(selectedMenu);
				const orgLogo = appState.app.transport.getCurrentOrganizationLogo() || "";
				const allPortfolioWideViews = actions.getAllPortfolioWideViews();

				if (isHome && viewSearchString) {
					requestAnimationFrame(() => {
						setViewSearchString("");
					});
				}

				const getDatabaseEnv = (): DatabaseEnv => {
					let env = DatabaseEnv.Prod;
					const baseUrl = appState.app.config.api.base;

					if (baseUrl.includes("dev")) {
						env = DatabaseEnv.Dev;
					} else if (baseUrl.includes("qa")) {
						env = DatabaseEnv.QA;
					}

					return env;
				};

				const databaseEnv = getDatabaseEnv();

				const envPillMaybe: ReactNode = [DatabaseEnv.Dev, DatabaseEnv.QA].includes(databaseEnv) && (
					<EnvPill
						$color={databaseEnv === DatabaseEnv.Dev ? colorPalette.libraryColors.red : colorPalette.libraryColors.eggYolk}
						onClick={Functions.stopPropagation}
					>
						{DatabaseEnv[databaseEnv]}
					</EnvPill>
				);

				const betaPill: ReactNode = (
					<EnvPill
						$color={colorPalette.primary.c500Primary}
						$fontSize={fontSize.sm}
						onClick={Functions.stopPropagation}
					>
						BETA
					</EnvPill>
				);

				return (
					<NavigationContainerStyled
						$isDocked={navBarState === "docked"}
						$isHome={isHome}
						$isNavBarHidden={isNavBarHidden}
					>
						<NavigationStyled
							$navBarState={navBarState}
							$isHome={isHome}
							ref={navigationRef}
							onClick={onNavBarClick}
						>
							<NavOrganizationDropdownContainerStyled $isHome={isHome}>
								<DropdownButtonV5
									showSearch={true}
									parentStyle={{flex: 1}}
									horizontalAlignment={HorizontalAlignment.outerRight}
									verticalAlignment={VerticalAlignment.top}
									width="350px"
									className="org-option"
									offsetX={8}
									offsetY={0}
									optionsZIndex={zIndex.popup}
									options={sortedOrganizations.map((o) => ({
										customElement: renderOrgDropdownItem(o, appState),
										label: o.name,
										onClick: () => onClickOrganizationSwitch(o),
										isActive: o.id === appState.organizationIdByAuthData,
									}))}
									button={
										<OrganizationTitleStyled
											$label={navBarState === "closed" && (appState.organization?.name ?? "")}
											$isMinimized={navBarState === "closed"}
										>
											{orgLogo ? (
												<img
													width={16}
													height={16}
													src={orgLogo}
													alt="organization thumbnail image"
												/>
											) : (
												<LogoIcon style={{minWidth: 16, height: 16}} />
											)}
											{navBarState !== "closed" && <h3>{appState.organization?.name ?? ""}</h3>}
										</OrganizationTitleStyled>
									}
								/>
								{navBarState === "docked" ? (
									<IconButtonV5
										IconComponent={SidebarCloseIcon}
										onClick={undockNavBar}
									/>
								) : (
									navBarState === "open" && (
										<IconButtonV5
											IconComponent={SidebarOpenIcon}
											onClick={dockNavBar}
										/>
									)
								)}
							</NavOrganizationDropdownContainerStyled>
							<SeparatorV5 isDocked={navBarState !== "closed"} />
							<NavigationMidContentStyled $onlyIcon={onlyIcon}>
								<NavMenuStyled>
									{mainNavMenusV5.map((menu, i) => (
										<NavigationMenuItem
											key={menu.label}
											onlyIcon={onlyIcon}
											isActive={appState.selectedMenu === menu.nav}
											{...menu}
											onClick={() => goApp(menu)}
										/>
									))}
								</NavMenuStyled>
								<SeparatorV5 isDocked={navBarState !== "closed"} />
								{isHome ? (
									<NavMenuStyled>
										<PortfolioSelector />
									</NavMenuStyled>
								) : (
									<>
										<NavMenuStyled>
											<PortfolioSelector />
											{secondaryMenu.map((menu, i) => (
												<NavigationMenuItem
													onClick={() => goApp(menu)}
													isActive={appState.selectedMenu === menu.nav}
													key={menu.label}
													onlyIcon={onlyIcon}
													small={!isHome}
													{...menu}
												/>
											))}
										</NavMenuStyled>
										<SeparatorV5 isDocked={navBarState !== "closed"} />
										{navBarState !== "closed" && (
											<>
												<ViewFind
													viewSearchString={viewSearchString}
													setViewSearchString={setViewSearchString}
													autoFocus={allPortfolioWideViews.length > 0}
												/>
												<NavigationViewSection
													openCreateViewPopup={openCreateViewPopup}
													onlyIcon={onlyIcon}
													isHome={isHome}
													onViewItemShareClick={onViewItemShareClick}
													onGlobalViewActionClick={onGlobalViewActionClick}
													onViewPopupOpenChange={setIsViewPopupOpen}
													onOpenReportPanel={openReportPanel}
													onWorkspaceRenameActiveChange={onWorkspaceRenameActiveChange}
													searchString={viewSearchString}
													viewCount={allPortfolioWideViews.length}
												/>
											</>
										)}
										{navBarState === "closed" && (
											<ViewSection
												openCreateViewPopup={openCreateViewPopup}
												isHome={isHome}
												onViewItemShareClick={onViewItemShareClick}
												onGlobalViewActionClick={onGlobalViewActionClick}
												onViewPopupOpenChange={setIsViewPopupOpen}
												onOpenReportPanel={openReportPanel}
												onWorkspaceRenameActiveChange={onWorkspaceRenameActiveChange}
												viewCount={allPortfolioWideViews.length}
											/>
										)}
									</>
								)}
							</NavigationMidContentStyled>
							<Flex
								$flex="1"
								style={{alignSelf: "normal"}}
							/>
							<SideBarWidgetsStyled
								$isHome={isHome}
								$navBarState={navBarState}
								ref={helpRef}
							>
								<div className="pillsContainer">
									{envPillMaybe}
									{betaPill}
								</div>
								{widgetMenus.map((menu) => (
									<NavigationMenuItem
										{...menu}
										key={menu.label}
										onlyIcon={!(isHome && navBarState !== "closed")}
										small={!isHome}
									/>
								))}
							</SideBarWidgetsStyled>
							{importOpen && (
								<FeatureImportPanelV5
									onClose={() => setImportOpen(false)}
									dataCyTitle="navigation"
								/>
							)}
							{isHelpOpen && (
								<TopBarDropdownV5
									elements={helpElements}
									parent={helpRef}
									offsetX={10}
									offsetY={0}
								/>
							)}
							<SpaceSelectorPopup divRef={spaceSelectorRef} />
							{createViewPopupType && (
								<CreateViewPopup
									onClose={closeCreateViewPopup}
									type={createViewPopupType}
									viewFolderIdToSaveNewViewInto={viewFolderIdToSaveNewViewInto}
									onCreateReportView={openReportPanel}
								/>
							)}
							{newReport && (
								<ReportPanelV5
									isReportView={true}
									report={newReport}
									onRunReport={onRunReport}
									favoriteViewFolderIdToSaveNewViewInto={favoriteViewFolderIdToSaveNewViewInto}
									viewFolderIdToSaveNewViewInto={viewFolderIdToSaveNewViewInto}
									onClose={(saveChanges: boolean, report: Report) => {
										setNewReport(null);
										setFavoriteViewFolderIdToSaveNewViewInto(null);
										setViewFolderIdToSaveNewViewInto("");
										if (!saveChanges) {
											report?.cancelEditing();
										}
									}}
								/>
							)}
							{viewItemForSharePanel && (
								<ViewItemSharingPopup
									viewItem={viewItemForSharePanel}
									onClose={() => setViewItemForSharePanel(null)}
								/>
							)}
							{globalViewSharingPopupState && (
								<GlobalSharingPopup
									viewItems={[globalViewSharingPopupState.view]}
									onClose={closeGlobalViewSharingPopup}
									viewActionType={globalViewSharingPopupState.viewActionType}
									isReports={globalViewSharingPopupState.view.ownFeature === XyiconFeature.Report}
									newName={globalViewSharingPopupState.newName}
								/>
							)}
							{isShortcutOpen && (
								<DomPortal destination={document.body}>
									<ShortcutWindowV5 onClose={onCloseShortcut} />
								</DomPortal>
							)}
						</NavigationStyled>
					</NavigationContainerStyled>
				);
			}}
		</Observer>
	);
};

const renderOrgDropdownItem = (org: Organization, appState: AppState) => {
	const logo = appState.app.transport.getOrganizationLogoById(org.id);

	return (
		<OrgItem>
			<div className="title">
				{logo ? (
					<img
						width={24}
						height={24}
						src={logo}
						alt={`${org.name} logo thumbnail`}
					/>
				) : (
					<LogoIcon />
				)}
				<span>{org.name}</span>
			</div>
			{appState.organizationIdByAuthData === org.id && <CheckboxInputV5 value={true} />}
		</OrgItem>
	);
};

const EnvPill = styled.div<{$color: string; $fontSize?: string}>`
	background-color: ${(props) => props.$color};
	border-radius: ${radius.xl};
	height: 24px;
	width: 40px;
	color: ${colorPalette.white};
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: default;
	font-size: ${(props) => props.$fontSize};
`;

const panelWidths = {
	home: "200px",
	workspace: "288px",
	minimized: "48px",
};

const NavigationContainerStyled = styled.div<{$isDocked: boolean; $isHome: boolean; $isNavBarHidden: boolean}>`
	${(props) =>
		props.$isNavBarHidden &&
		css`
			display: none;
		`}
	position: relative;
	width: ${(props) => (props.$isDocked ? (props.$isHome ? panelWidths.home : panelWidths.workspace) : panelWidths.minimized)};
	height: 100vh;
	min-width: ${panelWidths.minimized};
	z-index: ${zIndex.primaryNavColumn};
`;

const NavOrganizationDropdownContainerStyled = styled.div<{$isHome: boolean}>`
	${FlexCenterStyle};
	gap: ${baseDistance.xs};
	justify-content: space-between;
	border-radius: ${radius.sm};

	${DropdownButtonStyled} {
		height: 24px;
	}

	h3 {
		font-weight: ${fontWeight.bold};
		font-size: ${fontSize.md};
		overflow: hidden;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
	}

	.org-option {
		max-height: 400px;

		li {
			&:hover {
				background-color: ${colorPalette.gray.c200Light};
				color: ${colorPalette.gray.c950};
			}
		}
	}

	${(props) => {
		if (!props.$isHome) {
			return css`
				.IconButton:hover {
					background-color: ${colorPalette.blueGray.c200Light};
				}
			`;
		}
	}}
`;

const NavigationStyled = styled.div<TNavigationV5>`
	${VerticalFlexStyle}
	background-color: ${(props) => (props.$isHome ? colorPalette.white : colorPalette.blueGray.c100)};
	transition: ease-out width 0.2s;
	padding: ${(props) => (props.$navBarState === "closed" ? `${baseDistance.oh}` : `${baseDistance.sm}`)};
	box-sizing: border-box;
	gap: ${(props) => (props.$navBarState === "closed" ? `${baseDistance.oh}` : `${baseDistance.sm}`)};
	border-right: solid 1px ${colorPalette.gray.c300};
	cursor: pointer;
	width: ${(props) => (props.$navBarState === "docked" ? (props.$isHome ? panelWidths.home : panelWidths.workspace) : "100%")};
	position: absolute;
	z-index: 10;
	height: 100%;

	${NavOrganizationDropdownContainerStyled} > ${DropdownButtonStyled} {
		&:hover,
		&.isOpen {
			background-color: ${colorPalette.blueGray.c200Light};
		}
	}

	${NavOrganizationDropdownContainerStyled} {
		&:hover,
		&.isOpen {
			background-color: ${colorPalette.blueGray.c200Light};
		}
	}

	${NavOrganizationDropdownContainerStyled} > ${DropdownButtonStyled} {
		max-width: ${(props) => (props.$navBarState !== "closed" ? "calc(100% - 34px);" : "initial")};
	}

	${(props) => {
		if (props.$navBarState === "open") {
			return css`
				position: absolute;
				width: ${props.$isHome ? panelWidths.home : panelWidths.workspace};
				height: 100vh;
				z-index: 10;
			`;
		}
	}}

	${DropdownContentStyled} {
		left: 8px;
		top: -52px;
	}
`;

const OrganizationTitleStyled = styled.div<{$isMinimized: boolean; $label: string}>`
	${FlexCenterStyle}
	flex: 1;
	gap: ${baseDistance.sm};
	position: relative;
	border-radius: ${radius.sm};
	height: 24px;

	img {
		border-radius: ${radius.sm};
		background-color: ${colorPalette.white};
		margin: 0 ${baseDistance.xs};
	}

	${pseudoLabelTooltipStyle}
`;

const NavigationMidContentStyled = styled.div<{$onlyIcon: boolean}>`
	${VerticalFlexStyle};
	gap: ${baseDistance.sm};
	overflow-y: ${(props) => (props.$onlyIcon ? "initial" : "auto")};
	&::-webkit-scrollbar {
		display: ${(props) => (props.$onlyIcon ? "initial" : "none")};
	}
`;

const SideBarWidgetsStyled = styled.div<ISideBarWidgetsProps>`
	${(props) => (props.$isHome || (!props.$isHome && props.$navBarState === "closed") ? VerticalFlexStyle : FlexStyle)};
	${(props) => (!props.$isHome && props.$navBarState !== "closed" ? "flex-direction: row-reverse" : "")};
	${(props) => (props.$navBarState !== "closed" ? "justify-content: flex-end" : "")};
	gap: ${(props) => (props.$isHome ? baseDistance.sm : baseDistance.xs)};
	align-items: ${(props) => (props.$isHome && props.$navBarState !== "closed" ? "flext-start" : "center")};

	.pillsContainer {
		display: flex;
		justify-content: start;
		gap: ${baseDistance.sm};
		flex-direction: ${(props) => (props.$navBarState === "closed" ? "column" : "row-reverse")};
	}
`;

const SeparatorV5Styled = styled.div<{$isDocked?: boolean}>`
	${FlexCenterStyle}
	justify-content: center;
	align-self: normal;

	span {
		width: ${(props) => (!props.$isDocked ? "16px" : "100%")};
		height: 2px;
		border-radius: ${radius.sm};
		background-color: ${colorPalette.gray.c300};
	}
`;

const OrgItem = styled.div`
	display: flex;
	gap: ${baseDistance.sm};
	justify-content: space-between;
	width: 100%;

	.title {
		display: flex;
		gap: ${baseDistance.sm};
	}

	svg {
		min-width: 24px;
	}
`;
